import { fetchCustomer } from '@sophocles/entities/Customer';
import { fetchSiteGroup, siteGroupSelector } from '@sophocles/entities/SiteGroup';
import { fetchSiteGroupMemberForSiteAndSiteGroup } from '@sophocles/entities/SiteGroupMember/siteGroupMemberActions';
import { loginUserWithToken } from '@sophocles/features/auth/authActions';
import {
  authTokenSelector,
  getAuthUserSelector,
  isAuthenticatedSelector,
} from '@sophocles/features/auth/authSelectors';
import tokenService from '@sophocles/services/token';
import store from '@sophocles/store/redux-store';
import {
  determineIsAccounting,
  determineIsCrewManager,
  determineIsReadOnlySiteGroupUser,
  determineIsSales,
  determineIsSalesAdmin,
  determineIsShipping,
  determineIsTechSupport,
  determineIsTechnician,
  determineIsAtLeastSiteGroupAdmin,
  userPermCheck,
} from '@sophocles/utils/userPermissionUtils';

// The "home" path for a user, applied uniformly to everyone: users who
// can see more than one customer land on the customer list; users scoped
// to a single customer land on that customer's site-group list.
export const homePath = user => {
  if (userPermCheck(user, 'pliny.view_other_customers')) {
    return '/support/customers';
  }
  return `/c/${user.get('customer', '0')}/plants`;
};

// function to send any customer home
export const sendHome = navigate => {
  if (!navigate) {
    return true; // Assume this was called in error, and short-circuit
  }

  const user = getAuthUserSelector(store.getState());
  navigate(homePath(user), { replace: true });
};

// Guards return ``true`` (allow), ``false`` (permanent deny), or
// ``'transient'`` (couldn't determine right now — RequireAuth will
// retry before surfacing UI). Guards no longer call ``sendHome``
// directly: that side effect made transient cold-boot races on iOS
// resume look like permanent denials and silently kicked the user
// off the deep route. RequireAuth now drives the redirect/UX
// decision with full context.

export const determineIsStaff = () => {
  const user = getAuthUserSelector(store.getState());
  return user.get('is_superuser', false) || user.get('is_staff', false);
};

export const determineIsODLEngineering = (_, history) => {
  const user = getAuthUserSelector(store.getState());
  const groups = user.get('groups', []);
  if (user.get('is_superuser', false) || groups.indexOf(1) > -1) {
    return true;
  }
  if (history) {
    sendHome(history);
    return undefined;
  }
  return false;
};

// Staff, superusers, and ODL engineering (group 1) only — no site-group /
// customer admin fallback.
export const determineIsStaffOrEngineeringRoute = (_, history) => {
  const user = getAuthUserSelector(store.getState());
  const groups = user.get('groups', []);
  if (user.get('is_superuser', false) || user.get('is_staff', false) || groups.indexOf(1) > -1) {
    return true;
  }
  if (history) {
    sendHome(history);
    return undefined;
  }
  return false;
};

export const determineCanUpgradeFirmware = (_, history) => {
  const user = getAuthUserSelector(store.getState());
  if (user.get('is_superuser', false) || userPermCheck(user, 'pliny.can_upgrade_firmware')) {
    return true;
  }
  if (history) {
    sendHome(history);
    return undefined;
  }
  return false;
};

export const checkSalesAccess = (_, history) => {
  const user = getAuthUserSelector(store.getState());
  const groups = user.get('groups', []);
  const isSuper = user.get('is_superuser', false);
  const isSalesAdmin = determineIsSalesAdmin(groups);
  const isSalesUser = determineIsSales(groups);
  const isShipping = determineIsShipping(groups);
  if (isSuper || isSalesAdmin || isSalesUser || isShipping) {
    return true;
  }
  return false;
};

export const determineIsServicesUser = (_, history) => {
  const user = getAuthUserSelector(store.getState());

  const isSuper = user.get('is_superuser', false);
  const isStaff = user.get('is_staff', false);
  const isTechnician = determineIsTechnician(user.get('groups', []));
  const isTechSupport = determineIsTechSupport(user.get('groups', []));
  const isAccounting = determineIsAccounting(user.get('groups', []));
  const isCrewManager = determineIsCrewManager(user.get('groups', []));

  if (isSuper || (isStaff && (isTechnician || isTechSupport || isAccounting || isCrewManager))) {
    return true;
  }

  return false;
};

export const determineIsAccountingUser = (_, history) => {
  const user = getAuthUserSelector(store.getState());

  const isSuper = user.get('is_superuser', false);
  const isStaff = user.get('is_staff', false);
  const isAccounting = determineIsAccounting(user.get('groups', []));

  if (isSuper || (isStaff && isAccounting)) {
    return true;
  }

  return false;
};

export const determineIsTechSupportUser = (_, history) => {
  const user = getAuthUserSelector(store.getState());

  const isSuper = user.get('is_superuser', false);
  const isStaff = user.get('is_staff', false);
  const isTechSupport = determineIsTechSupport(user.get('groups', []));

  if (isSuper || (isStaff && isTechSupport)) {
    return true;
  }

  return false;
};

export const determineIsCrewManagerUser = (_, history) => {
  const user = getAuthUserSelector(store.getState());

  const isSuper = user.get('is_superuser', false);
  const isStaff = user.get('is_staff', false);
  const isCrewManager = determineIsCrewManager(user.get('groups', []));
  const isAccounting = determineIsAccounting(user.get('groups', []));

  if (isSuper || (isStaff && (isCrewManager || isAccounting))) {
    return true;
  }

  return false;
};

/**
 *
 * @param {object} props this is computedMatch from the router
 */
export const determineCustomerAccess = async props => {
  const user = getAuthUserSelector(store.getState());
  const { cust_id: custId } = props.params;

  // staff has full access to customer
  if (determineIsStaff()) {
    return true;
  }
  if (user.get('customer', '0') !== custId) {
    const res = await store.dispatch(fetchCustomer(custId));
    // ``res.error`` is true for transient fetch failures (network /
    // 5xx) and also for 4xx where the customer genuinely doesn't
    // exist for the user. We can't tell the two apart from the
    // current store shape, so treat it as transient and let
    // RequireAuth retry. A persistent failure surfaces the
    // "couldn't verify" UI.
    if (res.error) {
      return 'transient';
    }
    return true;
  }
  // user.customer matches the URL customer
  return true;
};

export const determineCustomerAccessNew = async props => {
  const user = getAuthUserSelector(store.getState());
  const { customerId } = props.params;

  if (determineIsStaff()) {
    return true;
  }
  if (user.get('customer', '0') !== customerId) {
    const res = await store.dispatch(fetchCustomer(customerId));
    if (res.error) {
      return 'transient';
    }
    return true;
  }
  return true;
};

export const determineInventoryAccess = async props => {
  const user = getAuthUserSelector(store.getState());
  const hasCustomerAccess = await determineCustomerAccessNew(props);
  if (hasCustomerAccess === 'transient') return 'transient';
  if (!hasCustomerAccess) return false;

  const canViewInventory = userPermCheck(user, 'odl_inventory.view_radiolocation');
  return canViewInventory;
};

export const determineServicesAccess = async props => {
  const user = getAuthUserSelector(store.getState());
  const { customerId } = props.params;

  if (determineIsStaff()) {
    return true;
  }
  if (user.get('customer', '0') !== customerId) {
    const res = await store.dispatch(fetchCustomer(customerId));
    if (res.error) {
      return 'transient';
    }
    return true;
  }
  return true;
};

export const determineSpecificPlantAccess = async props => {
  if (determineIsStaff()) {
    return true;
  }

  const customerResult = await determineCustomerAccess(props);
  if (customerResult === 'transient') return 'transient';
  if (!customerResult) return false;

  const siteGroups = siteGroupSelector(store.getState());
  const { sitegroup_id: siteGroupId, cust_id: custId } = props.params;

  const customerCompare = (customer, siteGroup) => customer == siteGroup.get('customer', '-1');

  // check store first
  const storeSiteGroup = siteGroups.get(siteGroupId.toString(), null);
  if (storeSiteGroup != null) {
    return customerCompare(custId, storeSiteGroup);
  }

  try {
    const res = await store.dispatch(fetchSiteGroup(siteGroupId));
    if (typeof res === 'undefined' || res === null) {
      // Empty result on cold boot is almost always a fetch race —
      // treat as transient.
      return 'transient';
    }
    const fetched = res.siteGroups.get(siteGroupId.toString());
    if (!fetched) return 'transient';
    return customerCompare(custId, fetched);
  } catch {
    return 'transient';
  }
};

export const determineIsAtLeastSiteGroupAdminRoute = async props => {
  if (determineIsStaff()) {
    return true;
  }

  const { sitegroup_id: siteGroupId, cust_id: custId } = props.params;
  const user = getAuthUserSelector(store.getState());

  try {
    const res = await store.dispatch(fetchSiteGroup(siteGroupId));
    if (typeof res === 'undefined' || res === null) {
      return 'transient';
    }
    const fetched = res.siteGroups.get(siteGroupId.toString());
    if (!fetched) return 'transient';
    if (custId != fetched.get('customer', '-1')) return false;
    return determineIsAtLeastSiteGroupAdmin(user.get('groups', []));
  } catch {
    return 'transient';
  }
};

// Device configuration's thresholds/status_requests sections (2PDC only)
// accept the wider legacy audience the old Voltage Thresholds / Config
// Status Request pages used — at-least-site-group-admin — while
// device/network/cic stay staff/engineering-only. The route can't tell
// whether a given site's unit is actually a 2PDC (that's only known once
// the schema response loads), so it admits the wider audience to the page
// for every unit type; the backend's per-section write predicates are the
// real enforcement, and a non-2PDC unit's schema has no thresholds/
// status_requests section for a site-group admin to write to.
export const determineCanConfigureDeviceRoute = async props => {
  if (determineIsStaffOrEngineeringRoute()) {
    return true;
  }
  return determineIsAtLeastSiteGroupAdminRoute(props);
};

export const determineSpecificStructureAccess = async props => {
  if (determineIsStaff()) {
    return true;
  }

  const { sitegroup_id: sitegroupId, site_id: siteId } = props.params;

  try {
    const res = await store.dispatch(fetchSiteGroupMemberForSiteAndSiteGroup(siteId, sitegroupId));
    if (res === undefined || res === null) {
      return 'transient';
    }
    const results = res.get('results');
    if (results.count() === 0) {
      // Could be a fresh cold boot where the membership cache is
      // still warming, or a true permission gap. Treat as transient
      // so RequireAuth retries; if it stays empty after retries
      // we surface the "couldn't verify" UI rather than silently
      // kicking the user to /plants.
      return 'transient';
    }
    return results.first().get('site_group') == sitegroupId;
  } catch {
    return 'transient';
  }
};

export const determineCanSeeSupport = () => {
  if (determineIsStaff()) {
    return true;
  }
  const user = getAuthUserSelector(store.getState());
  return userPermCheck(user, 'pliny.view_other_customers');
};

// View-only users (group 1054) may not see other users — they can
// still reach their own /user settings but the user list pages are
// blocked.  Staff and all non-view-only customer roles pass through;
// plant/customer access is still validated by the wrapped guard.
export const determineCanViewUsers = async props => {
  const user = getAuthUserSelector(store.getState());
  const groups = user.get('groups', []);
  if (!user.get('is_staff', false) && !user.get('is_superuser', false) && determineIsReadOnlySiteGroupUser(groups)) {
    return false;
  }
  return determineCustomerAccess(props);
};

export const determineCanViewUsersInPlant = async props => {
  const user = getAuthUserSelector(store.getState());
  const groups = user.get('groups', []);
  if (!user.get('is_staff', false) && !user.get('is_superuser', false) && determineIsReadOnlySiteGroupUser(groups)) {
    return false;
  }
  return determineSpecificPlantAccess(props);
};

export const determineCanViewRMAs = async props => {
  if (determineIsStaff()) return true;

  const plantAccess = await determineSpecificPlantAccess(props);
  if (plantAccess === 'transient') return 'transient';
  if (!plantAccess) return false;

  const user = getAuthUserSelector(store.getState());
  return userPermCheck(user, 'rma.view_returnmaterialauthorization');
};

/**
 *
 */
const checkCredentials = () => {
  if (tokenService.legacyToken) {
    tokenService.logout();
    window.location.href = '/login';
  }

  const linkingMode = localStorage.getItem('odl.auth.linking');
  if (linkingMode === 'true') {
    if (
      window.location.pathname != '/user' &&
      window.location.pathname != '/login' &&
      window.location.pathname != '/callback' &&
      window.location.pathname != '/'
    ) {
      window.location.href = '/user?tab=auth';
    }
  }

  const isAuth = isAuthenticatedSelector(store.getState());
  if (isAuth) {
    return Promise.resolve(true);
  }

  if (tokenService.idToken) {
    // We have a token but Redux is fresh (cold boot, no redux-persist).
    // Dispatch the login chain and *then* re-read isAuthenticated from
    // the store, so callers get a meaningful boolean instead of the
    // bare `undefined` that fetchAuthUser's promise chain resolves to.
    // Without this, MainLayout's authReady gate never flips true on
    // cold boot and route persistence never restores deep paths.
    return store.dispatch(loginUserWithToken()).then(() => {
      return isAuthenticatedSelector(store.getState());
    });
  }

  return Promise.resolve(false);
};

export default checkCredentials;
